/**
 * SSE client for assistant-service.
 *
 * Uses fetch, not `@/api` or EventSource: browser axios cannot stream a response
 * body, and the turn is a POST with a JSON body. `CONFIG.ASSISTANT_URL` is api-gateway
 * (`BASE_URL`) unless overridden for a local service. `Token.getToken()` is the
 * Cognito ID token the service requires.
 */
import { BEARER } from '@/api/api';
import { CONFIG } from '@/utils';
import Token from '@/utils/handlers/Token';

import { AssistantEvent, AssistantEventType } from '../assistant.types';

const EVENT_TYPES = new Set<unknown>(Object.values(AssistantEventType));

/** Thrown before the stream opens, so the HTTP status is meaningful. */
export class AssistantRequestError extends Error {
  public readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'AssistantRequestError';
    this.status = status;
  }
}

const isAssistantEvent = (value: unknown): value is AssistantEvent => {
  if (!value || typeof value !== 'object') return false;
  const { type } = value as { type?: unknown };
  return EVENT_TYPES.has(type);
};

interface StreamArgs {
  conversationId: string;
  text: string;
  signal?: AbortSignal;
  onEvent: (event: AssistantEvent) => void;
}

/**
 * POST one turn and call `onEvent` per SSE event. A mid-stream failure arrives
 * as an `error` event (status is already 200), not a rejection.
 */
export const streamAssistantTurn = async ({
  conversationId,
  text,
  signal,
  onEvent,
}: StreamArgs): Promise<void> => {
  const base = CONFIG.ASSISTANT_URL.replace(/\/$/, '');
  const url = `${base}/assistant/conversations/${encodeURIComponent(conversationId)}/messages`;

  const response = await fetch(url, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `${BEARER} ${Token.getToken() || ''}`,
    },
    // No participant_id: the service resolves the subject itself, in the admin's scope.
    body: JSON.stringify({ text }),
  });

  if (!response.ok || !response.body) {
    throw new AssistantRequestError(
      response.status,
      `Assistant request failed with status ${response.status}.`
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // Frames end with a blank line; keep the partial tail for the next read.
    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';

    frames.forEach((frame) => {
      const line = frame.split('\n').find((l) => l.startsWith('data:'));
      if (!line) return;
      try {
        const parsed: unknown = JSON.parse(line.slice('data:'.length).trim());
        // Unknown event types are ignored so new server events don't break the UI.
        if (isAssistantEvent(parsed)) onEvent(parsed);
      } catch {
        /* keep-alive or a frame we cannot parse — ignore */
      }
    });
  }
};

/** Delete a conversation's server-side history (the id is the LangGraph thread id). */
export const deleteConversation = async (conversationId: string): Promise<void> => {
  const base = CONFIG.ASSISTANT_URL.replace(/\/$/, '');
  await fetch(`${base}/assistant/conversations/${encodeURIComponent(conversationId)}`, {
    method: 'DELETE',
    headers: { Authorization: `${BEARER} ${Token.getToken() || ''}` },
  });
};
